// react
import React, { useEffect, useState } from "react";

import AIAssistantPortal from "./portals/AiAssistantPortal";

type AnchorData = {
	anchorElement: Element;
	elementId: string;
};

/**
 * Finds the anchor element associated with a data-ai-assistant element.
 * The anchor is expected to be a sibling or nearby element with id="anchor-ai-assistant-{elementId}".
 */
const findAnchorForElement = (element: Element, elementId: string): Element | null => {
	// First, try to find within the same goal-box (for goal discussions)
	const goalBox = element.closest(".goal-box");
	if (goalBox) {
		const anchor = goalBox.querySelector(`[id="anchor-ai-assistant-${elementId}"]`);
		if (anchor) return anchor;
	}

	// Try to find within the same question-block
	const questionBlock = element.closest(".question-block");
	if (questionBlock) {
		const anchor = questionBlock.querySelector(`[id="anchor-ai-assistant-${elementId}"]`);
		if (anchor) return anchor;
	}

	// Fallback: try parent container or global search
	const parent = element.parentElement;
	if (parent) {
		const anchor = parent.querySelector(`[id="anchor-ai-assistant-${elementId}"]`);
		if (anchor) return anchor;
	}

	// Last resort: global search (but scoped to avoid wrong anchor)
	return document.getElementById(`anchor-ai-assistant-${elementId}`);
};

const Review = (): React.ReactElement => {
	const [anchorDataList, setAnchorDataList] = useState<AnchorData[]>([]);

	useEffect(() => {
		const findAiAbleElements = (): void => {
			const elements = document.querySelectorAll('[data-ai-assistant="true"]');
			const anchors: AnchorData[] = [];

			elements.forEach((element) => {
				const rawId = element.getAttribute("id");
				if (!rawId) return;

				const elementId = rawId.replace("-text-area-question", "");
				const anchorElement = findAnchorForElement(element, elementId);

				if (anchorElement) {
					anchors.push({
						anchorElement,
						elementId,
					});
				}
			});

			setAnchorDataList(anchors);
		};

		findAiAbleElements();

		// Listen to DOM changes using MutationObserver
		const observer = new MutationObserver(() => {
			findAiAbleElements();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<>
			{anchorDataList.map((data, index) => (
				<AIAssistantPortal
					key={`portal-${data.elementId}-${index}`}
					anchorElement={data.anchorElement}
					elementId={data.elementId}
				/>
			))}
		</>
	);
};

export default Review;
